import { Pie, Show } from "components";
import React, { useState } from "react";

import { Treaty_treaty } from "_graphql/queries/__generated__/Treaty";
import { classNames } from "utils";
import numeral from "numeral";

const pieColors = [
  "#2563EB", // Blue
  "#0d9488", // Teal
  "#9333ea", // Purple
  "#CA8A04", // Yellow
  "#EF4444", // Red
];

const pieBgColors = [
  "bg-blue-600",
  "bg-teal-600",
  "bg-purple-600",
  "bg-yellow-600",
  "bg-red-600",
];

type Props = {
  treaty: Treaty_treaty | null | undefined;
  isProportional: boolean;
};

const ParticipationOverview = ({ treaty, isProportional }: Props) => {
  const [activeTab, setActiveTab] = useState(1);

  const participants = [
    ...((isProportional
      ? treaty?.treaty_participants
      : treaty?.treaty_participants?.filter(
          (el) => el?.layer_number === activeTab
        )) || []),
  ];

  return (
    <div className="bg-card-500 rounded-xl shadow-lg overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-700/50">
        <h3 className="text-xl font-semibold text-primary">
          Treaty Participation Overview
        </h3>
        <p className="mt-1 text-sm text-tertiary">
          View and manage treaty accounts and participation details
        </p>
      </div>
      <Show if={!isProportional}>
        <div className="block">
          <div className="border-b border-background-500 bg-card-500 px-6">
            <nav
              className="-mb-px flex space-x-8 items-center"
              aria-label="Tabs"
            >
              {JSON.parse(treaty?.layer_limit || "[]").map(
                (_activeTab: any, index: number) => (
                  <button
                    key={_activeTab.name}
                    onClick={() => setActiveTab(index + 1)}
                    className={classNames(
                      activeTab === index + 1
                        ? "border-gray-200 text-gray-300"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200",
                      "whitespace-nowrap flex py-4 px-1 border-b-2 font-medium text-sm"
                    )}
                    aria-current={activeTab === index + 1 ? "page" : undefined}
                  >
                    Layer {index + 1}
                  </button>
                )
              )}
              {/* <div className="h-6 w-px bg-gray-300" /> */}
            </nav>
          </div>
        </div>
      </Show>
      <Show if={participants?.length > 0}>
        <div className="p-6">
          <div className="bg-card-600/50 backdrop-blur-sm rounded-xl shadow-lg p-6">
            <div className="flex space-x-8">
              <div className="flex-1 space-y-4">
                {participants
                  ?.map((participant, index) => ({
                    color: pieColors.at(index % pieColors.length) || "#2563EB",
                    bgColor:
                      pieBgColors.at(index % pieBgColors.length) ||
                      "bg-blue-600",
                    id: participant?.treaty_participation_id,
                    symbol: participant?.reinsurer?.re_abbrv || "N/A",
                    label: participant?.reinsurer?.re_company_name || "Unknown",
                    value:
                      numeral(
                        participant?.treaty_participation_percentage || 0
                      ).value() || 0,
                  }))
                  ?.map?.((item: any) => (
                    <div
                      key={item?._id}
                      className="bg-card-700/50 rounded-lg p-3 flex items-center space-x-4 hover:bg-card-700/70 transition-colors duration-200"
                    >
                      <div
                        className={classNames(
                          item?.bgColor,
                          "rounded-lg p-3 shadow-lg"
                        )}
                      >
                        <span className="text-lg text-white font-medium">
                          {item?.symbol}
                        </span>
                      </div>
                      <div className="flex-1">
                        <dt className="text-sm font-medium text-tertiary">
                          {item?.label}
                        </dt>
                        <dd className="mt-1">
                          <p className="text-xl font-semibold text-primary">
                            {numeral(item?.value || 0).value()}%
                          </p>
                        </dd>
                      </div>
                    </div>
                  ))}
              </div>
              <div className="w-80 h-80">
                <Pie
                  data={
                    [
                      ...((isProportional
                        ? treaty?.treaty_participants
                        : treaty?.treaty_participants?.filter(
                            (el) => el?.layer_number === activeTab
                          )) || []),
                    ]?.map((participant, index) => ({
                      color:
                        pieColors.at(index % pieColors.length) || "#2563EB",
                      bgColor:
                        pieBgColors.at(index % pieBgColors.length) ||
                        "bg-blue-600",
                      id: participant?.treaty_participation_id || "",
                      symbol: participant?.reinsurer?.re_abbrv,
                      label:
                        participant?.reinsurer?.re_company_name || "Unknown",
                      value:
                        numeral(
                          participant?.treaty_participation_percentage || 0
                        ).value() || 0,
                    })) || []
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </Show>
      <Show if={participants?.length === 0}>
        <div className="bg-card-600/50 backdrop-blur-sm rounded-xl shadow-lg p-6 items-center justify-center flex flex-col space-y-4">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-700">
            <svg
              className="h-6 w-6 text-gray-300"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M17 20h5v-2a4 4 0 00-3-3.87M9 20h6M4 20h5M6 8a4 4 0 118 0 4 4 0 01-8 0z"
              />
            </svg>
          </div>
          <p className="text-sm text-tertiary">
            No participants found for this treaty.
          </p>
        </div>
      </Show>
    </div>
  );
};

export default ParticipationOverview;
